import type { SimConfig } from '../config/simConfig';
import type { BallState, PlayerState } from './state';
import type { PitchCondition, Vec2, Vec3 } from '../types';
import { DT } from '../config/simConfig';

// D110/D111 (HB-A/HB-B) — the aerial primitives. IP:382 names a "loft table per action/charge" that §17
// never tabulated; `solveAerialKick` IS that table, computed rather than enumerated: give it a distance
// and an apex floor and it inverts the §5.3 flight model for the (speed, elevation) that gets there.
// `interceptFlight` is the 3-D analogue of receiver.ts's ground march — ONE source of truth for where a
// flighted ball can be met, shared by the passer's lead solver, the bot receiver, the chaser and the
// keeper, so the passer and the receiver can never disagree about where the meeting is.
//
// PURE: no I/O, no wall clock, no Math.random, and — critically — NO state.rng draws, so the §7 normative
// draw order is untouched and no golden can drift from randomness (HB-P10).
//
// The sampler below mirrors physics/ball.ts::stepBall MINUS Magnus (a kick launches spinless — the same
// simplification sim/trajectory.ts::predictKickTrajectory already ships). Keep the three in lockstep.

/** One sampled flight: where it went, how high it got, and where it first hit the ground. */
export interface FlightSample {
  /** Horizontal distance from the launch point at first touchdown (m). */
  readonly dist: number;
  /** Landing point (sim coords). */
  readonly point: Vec2;
  /** Greatest height reached (m). */
  readonly apex: number;
  /** Ticks from launch to first touchdown. */
  readonly ticks: number;
}

const MAX_FLIGHT_TICKS = 150; // ~5 s — longer than any kick this game can produce

/**
 * Integrate a spinless launch from `origin` at (`speed`, `elev`, `yaw`) under the §5.3 model until the
 * ball first touches down, and report the range/apex. Allocation-light and bounded; the solver runs it
 * a couple of dozen times per lofted kick.
 */
export function simulateFlight(
  origin: Vec3,
  speed: number,
  elev: number,
  yaw: number,
  cfg: SimConfig,
  conditions: PitchCondition,
): FlightSample {
  const horiz = speed * Math.cos(elev);
  return flightFrom(origin, { x: horiz * Math.cos(yaw), y: horiz * Math.sin(yaw), z: speed * Math.sin(elev) }, cfg, conditions);
}

/**
 * Where a ball ALREADY in flight will first touch down (and how high it still gets on the way). The
 * client's landing ring and the AI's chase/keeper targets all read this, so every actor is looking at
 * the same predicted spot the sim will actually produce.
 */
export function flightFrom(origin: Vec3, vel: Vec3, cfg: SimConfig, conditions: PitchCondition): FlightSample {
  const cond = cfg.conditions[conditions];
  let px = origin.x;
  let py = origin.y;
  let pz = Math.max(0, origin.z);
  let vx = vel.x;
  let vy = vel.y;
  let vz = vel.z;
  let apex = pz;
  for (let t = 0; t < MAX_FLIGHT_TICKS; t++) {
    const grounded = pz <= 0 && vz <= 0;
    const sp = Math.hypot(vx, vy, vz);
    const kd = cfg.ball.KD_AIR * sp;
    let ax = -kd * vx;
    let ay = -kd * vy;
    const az = (grounded ? 0 : -cfg.ball.GRAVITY) - kd * vz;
    if (grounded) {
      ax -= cond.KF_ROLL * vx;
      ay -= cond.KF_ROLL * vy;
    }
    vx += ax * DT;
    vy += ay * DT;
    vz += az * DT;
    if (grounded && vz < 0) vz = 0;
    px += vx * DT;
    py += vy * DT;
    pz += vz * DT;
    if (pz > apex) apex = pz;
    if (pz <= 0) {
      // first touchdown — that's the "landing" the receiver runs onto
      return { dist: Math.hypot(px - origin.x, py - origin.y), point: { x: px, y: py }, apex, ticks: t + 1 };
    }
  }
  return { dist: Math.hypot(px - origin.x, py - origin.y), point: { x: px, y: py }, apex, ticks: MAX_FLIGHT_TICKS };
}

/** Where a launch first touches down (the landing ring the client draws, and the chaser's run target). */
export function predictLanding(
  origin: Vec3,
  speed: number,
  elev: number,
  yaw: number,
  cfg: SimConfig,
  conditions: PitchCondition,
): FlightSample {
  return simulateFlight(origin, speed, elev, yaw, cfg, conditions);
}

/**
 * The touchdown point of a live, airborne, free ball — null when there is nothing to mark (in someone's
 * possession, or already rolling on the deck). Presentation reads it for the landing ring; the AI reads
 * it for chase and keeper positioning. One prediction, so what the player sees is where the bots go.
 */
export function ballLanding(
  ball: Pick<BallState, 'pos' | 'vel' | 'possessor'>,
  cfg: SimConfig,
  conditions: PitchCondition,
  minZ = 0.3,
): Vec2 | null {
  if (ball.possessor !== null) return null;
  if (ball.pos.z <= minZ && ball.vel.z <= 0) return null; // grounded or barely off the deck — no arc to read
  return flightFrom(ball.pos, ball.vel, cfg, conditions).point;
}

/** A solved lofted kick: the launch speed and elevation `applyKicks` should fire. */
export interface AerialSolution {
  readonly speed: number;
  readonly elev: number;
}

/** D155: EXPORTED so the perf gate can pin the budget itself. It used to assert only that these two
 *  identifiers still APPEARED in `solveAerialKick.toString()`, which is insensitive to their values —
 *  the budget could quadruple and the test that exists to stop exactly that stayed green. */
export const SPEED_BISECTIONS = 18; // the plan's budget: ≤ 18 steps per elevation pass
export const ELEV_REFINEMENTS = 3; // outer passes that lift the arc until the apex floor is met
/** The solver's worst-case cost in flight samples: one range probe plus the bisection run, per pass. */
export const SOLVE_FLIGHT_SAMPLES_MAX = (ELEV_REFINEMENTS + 1) * (1 + SPEED_BISECTIONS);

/**
 * Invert the §5.3 flight for a kick that lands `distM` away having reached at least `apexM`.
 *
 * Seed: the closed-form ballistic pair (a drag-free arc of range R and apex A has tanθ = 4A/R), clamped
 * to the HIGH.MIN_ELEV…MAX_ELEV band. Then bisect the LAUNCH SPEED against the sampled model until the
 * range matches — drag makes the true range shorter than ballistic, so the seed alone always undershoots.
 * Finally, up to `ELEV_REFINEMENTS` outer passes lift the elevation if drag ate the apex (apex ∝ tanθ at
 * fixed range), each re-bisecting the speed.
 *
 * SATURATES rather than overshooting: if even HIGH.MAX_SPEED cannot carry `distM`, the kick fires at the
 * cap and simply lands short (§9.3's "quantizers saturate, never wrap" spirit, applied to a solver).
 */
export function solveAerialKick(distM: number, apexM: number, cfg: SimConfig, conditions: PitchCondition): AerialSolution {
  const H = cfg.actions.kick.HIGH;
  const target = Math.max(1, distM); // a kick at your own feet is still a kick — never NaN, never zero-range
  const apexFloor = Math.max(0.1, apexM);
  const origin: Vec3 = { x: 0, y: 0, z: 0 };
  const clampElev = (e: number): number => (e < H.MIN_ELEV ? H.MIN_ELEV : e > H.MAX_ELEV ? H.MAX_ELEV : e);

  let elev = clampElev(Math.atan((4 * apexFloor) / target));
  let speed = H.MAX_SPEED;
  for (let pass = 0; pass <= ELEV_REFINEMENTS; pass++) {
    // range is monotone in speed at a fixed elevation ⇒ bisect for the least speed that reaches `target`
    if (simulateFlight(origin, H.MAX_SPEED, elev, 0, cfg, conditions).dist <= target) {
      speed = H.MAX_SPEED; // out of range even at the cap — saturate short
    } else {
      let lo = 0;
      let hi = H.MAX_SPEED;
      for (let i = 0; i < SPEED_BISECTIONS; i++) {
        const mid = (lo + hi) / 2;
        if (simulateFlight(origin, mid, elev, 0, cfg, conditions).dist >= target) hi = mid;
        else lo = mid;
      }
      speed = hi;
    }
    if (pass === ELEV_REFINEMENTS) break;
    const apex = simulateFlight(origin, speed, elev, 0, cfg, conditions).apex;
    if (apex >= apexFloor || elev >= H.MAX_ELEV - 1e-9) break; // floor met (or the arc is already maxed)
    const lifted = clampElev(Math.atan(Math.tan(elev) * (apexFloor / Math.max(apex, 1e-6))));
    if (lifted <= elev + 1e-9) break; // no further lift available
    elev = lifted;
  }
  return { speed, elev };
}

/** Which aerial verb a ball offers a player standing at `from` — or null if it offers none. */
export type StrikeKind = 'VOLLEY' | 'HEADER';

/**
 * D113 — the aerial strike ENVELOPE, shared by the sim and the client's header-ready cue.
 *
 * The sim resolves the strike from this, and the reticle lights up from this, so the cue can never
 * promise a header the sim then refuses (or stay dark on one it would have given). Returns the verb the
 * ball is currently offering: a VOLLEY in the shin-to-chest band, a HEADER in the head band — or when
 * the ball is still above the head band but will DROP into it within the jump window, since otherwise
 * you could never head a ball that is over your head at the moment you must leave the ground.
 */
/**
 * D155 — is this ball one the SIM's aerial arm owns? A free ball at or above the volley band's floor.
 *
 * `armAerialIntent` keeps a declared SHOOT alive over such a ball (TAP_ARM/PT4-P11) and lets a second
 * press cancel it, so the client's D53 one-touch buffer must NOT also re-emit the bit — a re-emitted
 * hold gives the sim no rising edge, and the cancel press is swallowed for the whole 18-tick window.
 * The client had to make the same judgement and had no shared way to say it; this is that way.
 */
export function simOwnsAerialArm(ball: Pick<BallState, 'pos' | 'possessor'>, cfg: SimConfig): boolean {
  return ball.possessor === null && ball.pos.z >= cfg.actions.aerial.VOLLEY_Z[0];
}

export function strikeEnvelope(
  ball: Pick<BallState, 'pos' | 'vel' | 'possessor'>,
  from: Vec2,
  cfg: SimConfig,
  conditions: PitchCondition,
): StrikeKind | null {
  const A = cfg.actions.aerial;
  if (ball.possessor !== null) return null; // a carried ball is nobody's volley
  // D135: the horizontal reach gate applies to the INSTANTANEOUS bands only. It used to sit at the top
  // and return null outright, which quietly defeated the header's own lookahead as well: a ball
  // approaching horizontally was rejected before either window was consulted, so the lookahead only ever
  // fired for balls already overhead. That is why a driven cross "tunnelled" — it was never eligible at
  // any tick, not merely at the wrong ones.
  const inReachNow = Math.hypot(ball.pos.x - from.x, ball.pos.y - from.y) <= A.STRIKE_REACH;
  if (inReachNow && ball.pos.z >= A.VOLLEY_Z[0] && ball.pos.z < A.VOLLEY_Z[1]) return 'VOLLEY';
  if (inReachNow && ball.pos.z >= A.HEADER_Z[0] && ball.pos.z <= A.HEADER_Z[1]) return 'HEADER';
  // D135: the VOLLEY gains the header's lookahead. Its envelope was an instantaneous per-tick test, so a
  // real cross offered ~2–3 eligible ticks and a fast or steep ball tunnelled through the band between
  // two of them entirely. Same `aerialContact` the reticle cue calls, so sim and cue stay in lockstep.
  if (A.VOLLEY_WINDOW > 0 && aerialContact(ball, from, cfg, conditions, A.VOLLEY_Z, A.STRIKE_REACH, A.VOLLEY_WINDOW) !== null) return 'VOLLEY';
  if (aerialContact(ball, from, cfg, conditions, A.HEADER_Z, A.STRIKE_REACH, A.HEADER_WINDOW[1]) !== null) return 'HEADER';
  return null;
}

/**
 * The first tick within `maxTicks` at which the flighted ball is inside `band` vertically AND within
 * `reach` horizontally of the fixed point `from` — tick 0 meaning "right now". This is the aerial
 * strike's eligibility test (D113): it answers both "can I volley this on the spot?" (maxTicks 0) and
 * "will this drop onto my head if I jump now?" (maxTicks = the header window).
 *
 * A fixed point, not a moving player, on purpose: `HEADER_JUMP` has ACTION_SPEED_MUL 0, so a player who
 * commits to the jump does NOT get to run to the ball afterwards. Judging the jump against where he can
 * run would promise a contact the sim then refuses to deliver.
 */
export function aerialContact(
  ball: Pick<BallState, 'pos' | 'vel'>,
  from: Vec2,
  cfg: SimConfig,
  conditions: PitchCondition,
  band: readonly [number, number],
  reach: number,
  maxTicks: number,
): { tick: number; z: number } | null {
  const cond = cfg.conditions[conditions];
  let px = ball.pos.x;
  let py = ball.pos.y;
  let pz = Math.max(0, ball.pos.z);
  let vx = ball.vel.x;
  let vy = ball.vel.y;
  let vz = ball.vel.z;
  for (let t = 0; t <= maxTicks; t++) {
    if (pz >= band[0] && pz <= band[1] && Math.hypot(from.x - px, from.y - py) <= reach) return { tick: t, z: pz };
    const grounded = pz <= 0 && vz <= 0;
    const sp = Math.hypot(vx, vy, vz);
    const kd = cfg.ball.KD_AIR * sp;
    let ax = -kd * vx;
    let ay = -kd * vy;
    const az = (grounded ? 0 : -cfg.ball.GRAVITY) - kd * vz;
    if (grounded) {
      ax -= cond.KF_ROLL * vx;
      ay -= cond.KF_ROLL * vy;
    }
    vx += ax * DT;
    vy += ay * DT;
    vz += az * DT;
    if (grounded && vz < 0) vz = 0;
    px += vx * DT;
    py += vy * DT;
    pz += vz * DT;
    if (pz < 0) {
      pz = 0;
      if (vz < 0) {
        vz = -cond.REST * vz;
        vx *= 1 - cfg.ball.BOUNCE_GRIP;
        vy *= 1 - cfg.ball.BOUNCE_GRIP;
      }
    }
  }
  return null;
}

/** Where and when a player can meet a ball already in flight. */
export interface FlightIntercept {
  /** Ticks from now until the meeting. */
  readonly tick: number;
  /** Ground position of the meeting. */
  readonly point: Vec2;
  /** Ball height there (m). */
  readonly z: number;
}

export interface InterceptOptions {
  /** True when the player is ARMED for an aerial strike — head-height samples become playable (HB-D). */
  readonly arming?: boolean;
  /** Sampling horizon in ticks (default 90 — a cross hangs ~40). */
  readonly horizon?: number;
}

const SAMPLE_STRIDE = 2; // every 2 ticks over the horizon — 45 samples, plenty for 30 Hz geometry
const REACH_SPEED_MUL = 0.92; // a receiver never quite runs at MAX_SPEED (accel, turning)
const REACH_SLACK = 0.8; // + claim-radius-ish slack, matching receiver.ts's ground march

/**
 * The earliest tick at which `player` can both REACH the flighted ball and PLAY it there.
 *
 * "Playable" is the trap ceiling (CLAIM_Z × 1.5 — the same height `resolveTrap` gathers under), or, when
 * the player is arming an aerial strike, anything up to the top of the header band. Samples the REAL
 * flight (gravity + drag + bounce), so a ball that bounces on toward the receiver is found on its second
 * leg, not written off at the first touchdown. Returns null when no sample is reachable in time.
 *
 * PURE: reads `ball` and `player`, mutates neither.
 */
export function interceptFlight(
  ball: BallState,
  player: PlayerState,
  cfg: SimConfig,
  conditions: PitchCondition,
  opts: InterceptOptions = {},
): FlightIntercept | null {
  const cond = cfg.conditions[conditions];
  const horizon = opts.horizon ?? 90;
  const trapCeiling = cfg.possession.CLAIM_Z * 1.5; // = resolveTrap's zMax — one source of truth for "gatherable"
  const ceiling = opts.arming === true ? cfg.actions.aerial.HEADER_Z[1] : trapCeiling;
  const maxSpeed = cfg.movement.MAX_SPEED * REACH_SPEED_MUL;

  let px = ball.pos.x;
  let py = ball.pos.y;
  let pz = Math.max(0, ball.pos.z);
  let vx = ball.vel.x;
  let vy = ball.vel.y;
  let vz = ball.vel.z;
  for (let t = 1; t <= horizon; t++) {
    const grounded = pz <= 0 && vz <= 0;
    const sp = Math.hypot(vx, vy, vz);
    const kd = cfg.ball.KD_AIR * sp;
    let ax = -kd * vx;
    let ay = -kd * vy;
    const az = (grounded ? 0 : -cfg.ball.GRAVITY) - kd * vz;
    if (grounded) {
      ax -= cond.KF_ROLL * vx;
      ay -= cond.KF_ROLL * vy;
    }
    vx += ax * DT;
    vy += ay * DT;
    vz += az * DT;
    if (grounded && vz < 0) vz = 0;
    px += vx * DT;
    py += vy * DT;
    pz += vz * DT;
    if (pz < 0) {
      pz = 0;
      if (vz < 0) {
        vz = -cond.REST * vz; // the bounce continues the flight — a receiver can meet the second leg
        vx *= 1 - cfg.ball.BOUNCE_GRIP;
        vy *= 1 - cfg.ball.BOUNCE_GRIP;
      }
    }
    if (t % SAMPLE_STRIDE !== 0) continue;
    if (pz > ceiling) continue; // not playable at this sample (too high for the player's verb)
    const secs = t * DT;
    if (Math.hypot(player.pos.x - px, player.pos.y - py) <= secs * maxSpeed + REACH_SLACK) {
      return { tick: t, point: { x: px, y: py }, z: pz };
    }
  }
  return null;
}
